import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Clear as ClearIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    AddAPhoto as AddAPhotoIcon,
    AutoFixHigh as AutoFixHighIcon,
    CloudOff as CloudOffIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { initializeDB } from '../../../config/db';
import {
    JOB_STORE,
    EQUIPMENT_STORE,
    STATUS_DRAFT,
    STATUS_SUBMITTED
} from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import CameraCapture from '../../shared/CameraCapture';
import useDictation from '../../../hooks/useDictation';
import { useOnlineStatus } from '../../../hooks/useOnlineStatus';
import { generateUUID } from '../../../utils/uuid';
import { liftEvaluationService } from '../../../services/liftEvaluationService';
import {
    RIGGING_CATEGORIES,
    WEIGHT_SOURCES,
    GROUND_TYPE,
    EQUIPMENT_SOURCE,
    defaultRequestedBy,
    CRANE_CODE_PREFIX,
    ASME_NOTE,
    MAX_PHOTOS,
    MAX_RECIPIENTS,
    isValidEmail,
    emptyRiggingItems,
    emptyRiggingRow,
    normalizeRiggingItems,
    calcTotalWeight,
    isInspectionOverdue,
    formatWeight,
    parseNumber
} from './liftEvaluationSchema';

/**
 * Ужимаем снимок до отправки. Двенадцать фото с камеры айпада — это десятки
 * мегабайт: раздувается и черновик в IndexedDB, и тело запроса. 1200 px по
 * длинной стороне хватает, чтобы разобрать расстановку крана.
 */
const PHOTO_MAX_DIM = 1200;
const PHOTO_QUALITY = 0.6;

function shrinkDataUrl(dataUrl, maxDim = PHOTO_MAX_DIM, quality = PHOTO_QUALITY) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
                const ratio = Math.min(maxDim / width, maxDim / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = dataUrl;
    });
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => shrinkDataUrl(e.target.result).then(resolve, reject);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/** Фото могло быть сохранено как строка (до появления подписей) — читаем оба вида. */
const photoOf = (photo) => (typeof photo === 'string' ? { path: photo, caption: '' } : (photo || { path: '', caption: '' }));

/** Названия полей для сообщения о незаполненном: форма длинная, «Validation Error» без списка бесполезен. */
const FIELD_LABELS = {
    requestedByName: 'Name', jobCode: 'Job', liftDate: 'Date',
    qualifiedRiggerName: 'Qualified Rigger', certifiedOperatorName: 'Certified Operator',
    craneType: 'Type of Crane', craneManufacturer: 'Manufacturer', craneModel: 'Model No.',
    craneSerialNo: 'Serial Number', annualInspectionDate: 'Latest Annual Inspection',
    itemsDescription: 'Items to be lifted', weightSource: 'Weight source', objectWeight: 'A — Weight of object',
    cgMarked: 'CG marked onto load', riggingWeightEstimate: 'B — Estimated rigging weight',
    maxOperatingRadius: 'A. Maximum Operating Radius', plannedOperatingRadius: 'B. Planned Operating Radius',
    allowableLoadAtMaxRadius: 'C. Allowable load at max radius', clearanceToFacilities: 'F. Clearance to Facilities',
    clearPathForLoadMovement: 'G. Clear Path for Load Movement', groundAreaType: 'Ground area',
    bearingCapacity: 'Bearing Capacity', matsRequired: 'Mats Required', undergroundUtilities: 'Underground Utilities',
    riggingInspection: 'Rigging Inspection', dailyCraneInspection: 'Daily Crane Inspection completed',
    supervisorSignature: 'Supervisor signature', riggerSignature: 'Rigger signature',
    operatorSignature: 'Operator signature',
};

const Section = ({ title, subtitle, children, action }) => (
    <Card variant="outlined" sx={{ px: 1.5, py: 1.25, mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box>
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>{title}</Typography>
                {subtitle && <Typography variant="caption" color="text.secondary" lineHeight={1.2}>{subtitle}</Typography>}
            </Box>
            {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
        </Box>
        {children}
    </Card>
);

/** Поле с надиктовкой — как в JHA: микрофон в конце поля. */
const DictationField = ({ field, label, value, onChange, dictation, disabled, rows = 3, ...rest }) => {
    const active = dictation.supported && dictation.activeField === field;

    return (
        <TextField
            fullWidth
            size="small"
            multiline
            rows={rows}
            label={label}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            InputProps={{
                endAdornment: dictation.supported && !disabled && (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <IconButton
                            onClick={() => dictation.toggle(field, (text) => onChange(`${value || ''}${value ? ' ' : ''}${text}`))}
                            edge="end"
                            color={active ? 'error' : 'default'}
                            size="small"
                            title={active ? 'Stop dictation' : 'Dictate'}
                        >
                            {active ? <MicIcon /> : <MicOffIcon />}
                        </IconButton>
                    </InputAdornment>
                )
            }}
            {...rest}
        />
    );
};

/** Y / N / не заполнено — на бумаге это одна ячейка «y/n». */
const YesNoField = ({ label, value, onChange, disabled, required, error }) => (
    <FormControl fullWidth size="small" disabled={disabled} required={required} error={error}>
        <InputLabel shrink>{label}</InputLabel>
        <Select
            value={value === null || value === undefined ? '' : String(value)}
            label={label}
            displayEmpty
            notched
            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}
        >
            <MenuItem value=""><em>Not answered</em></MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
        </Select>
    </FormControl>
);

/**
 * Пара «y/n + комментарий» одной строкой — так этот блок размечен на бумаге.
 *
 * question — для вопросов, которые на бланке записаны целой фразой: в подписи
 * селекта такой текст обрезается многоточием, поэтому он идёт строкой над полями.
 */
const YesNoWithComment = ({ label, question, value, onChange, comment, onCommentChange, field, dictation, disabled, required, error }) => (
    <Box>
        {question && (
            <Typography
                variant="body2"
                sx={{ mb: 0.75, color: error ? 'error.main' : 'text.primary' }}
            >
                {question}{required && ' *'}
            </Typography>
        )}
        <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={12} md={question ? 3 : 4}>
                <YesNoField
                    label={question ? 'Answer' : label}
                    value={value} onChange={onChange}
                    disabled={disabled} required={required} error={error}
                />
            </Grid>
            <Grid item xs={12} md={question ? 9 : 8}>
                <DictationField
                    field={field} label="Comments" rows={1}
                    value={comment} onChange={onCommentChange}
                    dictation={dictation} disabled={disabled}
                />
            </Grid>
        </Grid>
    </Box>
);

/** Читаемый статус допуска рядом с именем человека. */
const CertChip = ({ certification, loading, offline }) => {
    if (loading) return <Chip size="small" icon={<CircularProgress size={12} />} label="Checking…" />;
    if (offline) return <Chip size="small" icon={<CloudOffIcon />} label="Needs connection" variant="outlined" />;
    if (!certification) return <Chip size="small" color="warning" label="No certification on file" />;

    const map = {
        valid: { color: 'success', label: `Valid to ${certification.expirationDate || '—'}` },
        expiring_soon: { color: 'warning', label: `Expires ${certification.expirationDate || '—'}` },
        expired: { color: 'error', label: `EXPIRED ${certification.expirationDate || ''}`.trim() },
        no_expiry: { color: 'default', label: 'No expiry on file' },
    };
    const view = map[certification.status] || map.no_expiry;

    return <Chip size="small" color={view.color} label={view.label} />;
};

const LiftEvaluationForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid');
    // Форму могли открыть из JHA — тогда джоб, дата и привязка приходят оттуда,
    // а после отправки надо вернуться на тот же шаг JHA, а не в список форм.
    const jhaLocalId = searchParams.get('jhaLocalId');
    const returnTo = searchParams.get('returnTo');
    const isOnline = useOnlineStatus();

    const sigPadRef = useRef(null);
    const saveTimeoutRef = useRef(null);
    const dictationFieldRef = useRef(null);

    const [jobs, setJobs] = useState([]);
    const [cranes, setCranes] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [signatureField, setSignatureField] = useState(null);
    const [employeeTarget, setEmployeeTarget] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [certLoading, setCertLoading] = useState({ rigger: false, operator: false });
    const [certOffline, setCertOffline] = useState({ rigger: false, operator: false });
    const [resolvingIdentity, setResolvingIdentity] = useState(false);
    const [manualEmail, setManualEmail] = useState('');
    const [inspectionMissing, setInspectionMissing] = useState(false);
    const [inspectionLookup, setInspectionLookup] = useState(false);

    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation();
    const [activeDictationField, setActiveDictationField] = useState(null);

    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        status: STATUS_DRAFT,
        jhaLocalId: jhaLocalId || null,

        requestedByName: defaultRequestedBy(),
        jobCode: searchParams.get('jobCode') || '',
        jobName: '',
        jobLocation: '',
        liftDate: searchParams.get('liftDate') || dayjs().format('YYYY-MM-DD'),

        qualifiedRiggerName: '',
        riggerCertCourse: '',
        riggerCertCompleted: '',
        riggerCertExpires: '',
        riggerCertStatus: '',
        certifiedOperatorName: '',
        operatorCertCourse: '',
        operatorCertCompleted: '',
        operatorCertExpires: '',
        operatorCertStatus: '',

        equipmentSource: EQUIPMENT_SOURCE.INTERNAL,
        equipmentId: null,
        equipmentCode: '',
        craneType: '',
        craneManufacturer: '',
        craneModel: '',
        craneSerialNo: '',
        annualInspectionDate: '',

        itemsDescription: '',

        weightSource: '',
        objectWeight: '',

        cgMarked: null,
        cgComment: '',

        riggingItems: emptyRiggingItems(),
        riggingWeightEstimate: '',

        maxOperatingRadius: '',
        plannedOperatingRadius: '',
        allowableLoadAtMaxRadius: '',
        clearanceToFacilities: '',
        clearPathForLoadMovement: null,
        clearPathComment: '',

        groundAreaType: GROUND_TYPE.SOIL,
        bearingCapacity: '',
        matsRequired: null,
        matsSizeAndNumber: '',
        matsComment: '',
        undergroundUtilities: null,
        undergroundUtilitiesComment: '',

        liftingOperationPhotos: [],
        liftingOperationNotes: '',

        riggingInspection: null,
        riggingInspectionComment: '',
        dailyCraneInspection: null,
        dailyCraneInspectionComment: '',

        supervisorName: '',
        supervisorSignature: '',
        riggerSignature: '',
        operatorSignature: '',
        customerName: '',
        customerSignature: '',
        safetyName: '',
        safetySignature: '',

        emailRecipients: [],
    });

    // ─── Загрузка справочников и черновика ───────────────────────────────
    useEffect(() => {
        loadJobs();
        loadCranes();
    }, []);

    useEffect(() => {
        if (uuid) loadFormData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(JOB_STORE, 'readonly');
            const all = await tx.objectStore(JOB_STORE).getAll();
            setJobs((all || []).sort((a, b) => (b.Job || '').localeCompare(a.Job || '')));
        } catch (error) {
            console.error('Error loading jobs:', error);
        }
    };

    const loadCranes = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(EQUIPMENT_STORE, 'readonly');
            const all = await tx.objectStore(EQUIPMENT_STORE).getAll();
            // Форма сделана под краны: только техника с префиксом «CR ».
            const list = (all || [])
                .filter((e) => (e.equipment || '').startsWith(CRANE_CODE_PREFIX) && e.status !== false)
                .sort((a, b) => (a.equipment || '').localeCompare(b.equipment || ''));
            setCranes(list);
        } catch (error) {
            console.error('Error loading cranes:', error);
        }
    };

    const loadFormData = async () => {
        try {
            const data = await liftEvaluationService.getFromIndexedDB(uuid);
            if (data) {
                setForm((prev) => ({
                    ...prev,
                    ...data,
                    riggingItems: normalizeRiggingItems(data.riggingItems),
                }));
                setSubmitted(data.status === STATUS_SUBMITTED);
            }
        } catch (error) {
            console.error('Error loading form data:', error);
        }
    };

    const queueSave = (updatedForm) => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await liftEvaluationService.saveToIndexedDB(updatedForm);
            } catch (error) {
                console.error('Error saving to IndexedDB:', error);
            }
        }, 500);
    };

    const update = (patch) => {
        setForm((prev) => {
            const updated = { ...prev, ...patch };
            queueSave(updated);
            return updated;
        });
    };

    // ─── Производные значения ────────────────────────────────────────────
    const totalWeight = useMemo(
        () => calcTotalWeight(form.objectWeight, form.riggingWeightEstimate),
        [form.objectWeight, form.riggingWeightEstimate]
    );
    const inspectionOverdue = useMemo(
        () => isInspectionOverdue(form.annualInspectionDate, form.liftDate),
        [form.annualInspectionDate, form.liftDate]
    );

    const selectedJob = useMemo(
        () => jobs.find((j) => j.Job === form.jobCode) || null,
        [jobs, form.jobCode]
    );

    const selectedCrane = useMemo(
        () => cranes.find((c) => c.id === form.equipmentId) || null,
        [cranes, form.equipmentId]
    );

    // Кнопка разбора нужна, только когда в справочнике что-то есть, а имени/модели нет.
    const canResolveIdentity =
        !!selectedCrane &&
        selectedCrane.identity?.source !== 'external' &&
        !!selectedCrane.category &&
        (!form.craneManufacturer || !form.craneModel || !form.craneSerialNo);

    // Джоб из JHA приходит одним номером — дотягиваем название и заказчика,
    // когда справочник джобов загрузился.
    useEffect(() => {
        if (!form.jobCode || form.jobName || jobs.length === 0) return;
        const job = jobs.find((j) => j.Job === form.jobCode);
        if (!job) return;
        update({
            jobName: job.JobName || '',
            jobLocation: job.Location || job.location || '',
            ...((job.Customer || job.customer) && !form.customerName
                ? { customerName: job.Customer || job.customer }
                : {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs, form.jobCode]);

    // ─── Диктовка ────────────────────────────────────────────────────────
    const toggleDictation = async (field, apply) => {
        if (!dictationSupported) return;

        if (isListening && activeDictationField === field) {
            stopDictation();
            setActiveDictationField(null);
            dictationFieldRef.current = null;
            return;
        }
        if (isListening) stopDictation();

        dictationFieldRef.current = field;
        setActiveDictationField(field);
        await startDictation({
            onResult: (transcript) => {
                if (transcript && dictationFieldRef.current === field) apply(transcript);
            },
            onEnd: () => { setActiveDictationField(null); dictationFieldRef.current = null; },
            onError: () => { setActiveDictationField(null); dictationFieldRef.current = null; },
        });
    };

    const dictation = { supported: dictationSupported, activeField: activeDictationField, toggle: toggleDictation };

    // ─── Джоб и кран ─────────────────────────────────────────────────────
    const handleJobChange = (event, job) => {
        const customer = job?.Customer || job?.customer || '';
        update({
            jobCode: job?.Job || '',
            jobName: job?.JobName || '',
            jobLocation: job?.Location || job?.location || '',
            ...(customer && !form.customerName ? { customerName: customer } : {}),
        });
    };

    const handleCraneChange = (event, crane) => {
        if (!crane) {
            update({ equipmentId: null, equipmentCode: '' });
            setInspectionMissing(false);
            return;
        }

        const identity = crane.identity || {};
        const inspection = crane.lastAnnualInspectionDate || '';
        // Дату чужого крана оставлять нельзя: под ней стоят три подписи, и по ней
        // же считается просрочка. Нет даты в справочнике — поле пустое и об этом
        // сказано прямо.
        setInspectionMissing(!inspection);

        update({
            equipmentId: crane.id,
            equipmentCode: crane.equipment || '',
            // Описание из справочника — это и есть «Type of Crane» на бумаге.
            craneType: crane.description || '',
            craneManufacturer: identity.manufacturer || '',
            craneModel: identity.model || '',
            craneSerialNo: identity.serialNo || '',
            annualInspectionDate: inspection,
        });

        // Справочник на планшете мог быть скачан до того, как в записях появилась
        // дата инспекции. Тогда спрашиваем сервер напрямую, а не просим человека.
        if (!inspection && isOnline) {
            refreshCraneFromServer(crane.id);
        }
    };

    /**
     * Догоняем запись техники с сервера, когда в локальном справочнике у крана
     * нет даты инспекции. Молча: для человека это выглядит как «дата всё-таки
     * подставилась», а не как отдельное действие.
     */
    const refreshCraneFromServer = async (equipmentId) => {
        setInspectionLookup(true);
        try {
            const fresh = await liftEvaluationService.fetchEquipment(equipmentId);
            if (!fresh) return;

            const identity = fresh.identity || {};
            const inspection = fresh.lastAnnualInspectionDate || '';
            setInspectionMissing(!inspection);

            setForm((prev) => {
                // Пока ходили на сервер, кран могли сменить — тогда ответ уже не про него.
                if (prev.equipmentId !== equipmentId) return prev;

                const updated = {
                    ...prev,
                    annualInspectionDate: inspection || prev.annualInspectionDate,
                    craneManufacturer: prev.craneManufacturer || identity.manufacturer || '',
                    craneModel: prev.craneModel || identity.model || '',
                    craneSerialNo: prev.craneSerialNo || identity.serialNo || '',
                };
                queueSave(updated);
                return updated;
            });

            await loadCranes();
        } catch (error) {
            // Нет связи или запись пропала — остаётся ручной ввод, о нём уже сказано.
            console.error('Could not refresh equipment record:', error);
        } finally {
            setInspectionLookup(false);
        }
    };

    const handleResolveIdentity = async () => {
        if (!selectedCrane) return;

        setResolvingIdentity(true);
        try {
            const result = await liftEvaluationService.resolveEquipmentIdentity(selectedCrane.id);
            const identity = result?.data?.identity || {};
            update({
                craneManufacturer: form.craneManufacturer || identity.manufacturer || '',
                craneModel: form.craneModel || identity.model || '',
                craneSerialNo: form.craneSerialNo || identity.serialNo || '',
            });
            await loadCranes();
            Swal.fire({
                icon: 'success',
                title: 'Pulled from equipment record',
                text: 'Check the values and correct them if needed — what you leave here is remembered.',
                timer: 3500,
                showConfirmButton: false,
            });
        } catch (error) {
            const message = error.response?.status === 422
                ? 'Nothing could be read from the equipment record. Please fill the fields in manually.'
                : 'Could not reach the server. Please fill the fields in manually.';
            Swal.fire({ icon: 'info', title: 'Manual entry needed', text: message });
        } finally {
            setResolvingIdentity(false);
        }
    };

    /**
     * То, что человек дописал руками, отправляем в справочник — чтобы в следующий раз
     * подставилось само. Молча: это удобство, а не действие пользователя.
     */
    const rememberIdentityIfNeeded = async () => {
        if (!selectedCrane || form.equipmentSource !== EQUIPMENT_SOURCE.INTERNAL) return;

        const identity = selectedCrane.identity || {};
        if (identity.source === 'external') return;

        const changed =
            (form.craneManufacturer || '') !== (identity.manufacturer || '') ||
            (form.craneModel || '') !== (identity.model || '') ||
            (form.craneSerialNo || '') !== (identity.serialNo || '');

        if (!changed) return;

        try {
            await liftEvaluationService.saveEquipmentIdentity(selectedCrane.id, {
                manufacturer: form.craneManufacturer,
                model: form.craneModel,
                serialNo: form.craneSerialNo,
            });
        } catch (error) {
            console.error('Could not remember equipment identity:', error);
        }
    };

    // ─── Люди и допуски ──────────────────────────────────────────────────
    const fetchCertification = async (role, fullName) => {
        if (!fullName) return;

        setCertOffline((prev) => ({ ...prev, [role]: false }));
        setCertLoading((prev) => ({ ...prev, [role]: true }));

        try {
            const data = await liftEvaluationService.fetchCertifications(fullName);
            const certification = role === 'rigger' ? data.rigger : data.operator;

            update({
                [`${role}CertCourse`]: certification?.course || '',
                [`${role}CertCompleted`]: certification?.dateComplete || '',
                [`${role}CertExpires`]: certification?.expirationDate || '',
                [`${role}CertStatus`]: certification?.status || '',
            });
        } catch (error) {
            // Офлайн — не ошибка: поля остаются пустыми и заполняются руками.
            setCertOffline((prev) => ({ ...prev, [role]: true }));
        } finally {
            setCertLoading((prev) => ({ ...prev, [role]: false }));
        }
    };

    /** Имя подписанта живёт в одном поле формы, но править его можно из обоих мест. */
    const SIGNER_FIELDS = {
        supervisor: 'supervisorName',
        rigger: 'qualifiedRiggerName',
        operator: 'certifiedOperatorName',
        customer: 'customerName',
        safety: 'safetyName',
    };

    const signerName = (role) => form[SIGNER_FIELDS[role]] || '';

    const setSignerName = (role, name) => {
        update({ [SIGNER_FIELDS[role]]: name });
        // Сменили риггера или оператора — это другой человек, допуск перезапрашиваем.
        if (role !== 'rigger' && role !== 'operator') return;
        if (name) {
            fetchCertification(role, name);
        } else {
            update({
                [`${role}CertCourse`]: '', [`${role}CertCompleted`]: '',
                [`${role}CertExpires`]: '', [`${role}CertStatus`]: '',
            });
        }
    };

    const handleEmployeeSelect = async (employee) => {
        const target = employeeTarget;
        setEmployeeTarget(null);
        if (!employee || !target) return;

        const fullName = employee.FullName || employee.fullName || '';

        if (target === 'recipient') {
            addRecipient(fullName, employee.email);
        } else if (target === 'requestedBy') {
            update({ requestedByName: fullName });
        } else if (SIGNER_FIELDS[target]) {
            setSignerName(target, fullName);
        }
    };

    // ─── Такелаж ─────────────────────────────────────────────────────────
    const addRiggingRow = (category, afterIndex) => {
        setForm((prev) => {
            const items = [...(prev.riggingItems || [])];
            items.splice(afterIndex + 1, 0, emptyRiggingRow(category));
            const updated = { ...prev, riggingItems: items };
            queueSave(updated);
            return updated;
        });
    };

    const removeRiggingRow = (index) => {
        setForm((prev) => {
            const items = (prev.riggingItems || []).filter((_, i) => i !== index);
            const updated = { ...prev, riggingItems: items };
            queueSave(updated);
            return updated;
        });
    };

    const updateRiggingRow = (index, patch) => {
        setForm((prev) => {
            const items = [...(prev.riggingItems || [])];
            items[index] = { ...items[index], ...patch };
            const updated = { ...prev, riggingItems: items };
            queueSave(updated);
            return updated;
        });
    };

    // ─── Фото ────────────────────────────────────────────────────────────
    const handlePhotoCapture = async (payload) => {
        setCameraOpen(false);
        if (!payload) return;

        if ((form.liftingOperationPhotos || []).length >= MAX_PHOTOS) {
            Swal.fire({ icon: 'info', title: 'Photo limit reached', text: `You can attach up to ${MAX_PHOTOS} photos.` });
            return;
        }

        try {
            const dataUrl = payload.file
                ? await fileToDataUrl(payload.file)
                : await shrinkDataUrl(payload.dataUrl);
            // Пара «снимок + подпись»: без подписи по фото не понять, что за ракурс.
            update({ liftingOperationPhotos: [...(form.liftingOperationPhotos || []), { path: dataUrl, caption: '' }] });
        } catch (error) {
            console.error('Photo error:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not attach the photo' });
        }
    };

    const removePhoto = (index) => {
        update({ liftingOperationPhotos: (form.liftingOperationPhotos || []).filter((_, i) => i !== index) });
    };

    const updatePhotoCaption = (index, caption) => {
        setForm((prev) => {
            const photos = [...(prev.liftingOperationPhotos || [])];
            photos[index] = { ...photoOf(photos[index]), caption };
            const updated = { ...prev, liftingOperationPhotos: photos };
            queueSave(updated);
            return updated;
        });
    };

    // ─── Получатели ──────────────────────────────────────────────────────
    const addRecipient = (name, email) => {
        if (!email) {
            Swal.fire({ icon: 'info', title: 'No email address', text: `${name || 'This person'} has no email on file.` });
            return false;
        }
        if (!isValidEmail(email)) {
            Swal.fire({ icon: 'error', title: 'Invalid email', text: `“${email}” is not a valid email address.` });
            return false;
        }

        const current = form.emailRecipients || [];
        // Молча ничего не пропадает: на каждый отказ человек видит причину.
        if (current.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
            Swal.fire({ icon: 'info', title: 'Already added', text: `${email} is already in the list.` });
            return false;
        }
        if (current.length >= MAX_RECIPIENTS) {
            Swal.fire({
                icon: 'warning',
                title: 'Recipient limit reached',
                text: `You can add up to ${MAX_RECIPIENTS} recipients. The office list is sent to on top of these.`,
            });
            return false;
        }

        update({ emailRecipients: [...current, { name: name || email, email }] });
        return true;
    };

    const addManualRecipient = () => {
        if (addRecipient(manualEmail.trim(), manualEmail.trim())) {
            setManualEmail('');
        }
    };

    const removeRecipient = (email) => {
        update({ emailRecipients: (form.emailRecipients || []).filter((r) => r.email !== email) });
    };

    // ─── Подписи ─────────────────────────────────────────────────────────
    const handleSignatureAccept = (signatureData) => {
        let signature = signatureData;
        if (typeof signatureData !== 'string') {
            signature = sigPadRef.current?.toDataURL() || '';
        }
        if (signatureField) update({ [`${signatureField}Signature`]: signature });
        setSignatureField(null);
    };

    /**
     * Колонка подписи: имя с выбором сотрудника и площадка фиксированной высоты —
     * иначе поставленная подпись меняет высоту колонки и блок разъезжается.
     */
    /**
     * Одна подпись: имя с выбором сотрудника и площадка фиксированной высоты.
     * Роль вынесена в подпись поля — на планшете отдельная строка-заголовок
     * съедала высоту, а подписей пять.
     */
    const renderSignature = (role, label, optional = false) => (
        <Grid container spacing={1} alignItems="center">
            <Grid item xs={7} sm={6}>
                <TextField
                    fullWidth
                    size="small"
                    label={optional ? `${label} (optional)` : label}
                    InputLabelProps={{ shrink: true }}
                    value={signerName(role)}
                    onChange={(e) => setSignerName(role, e.target.value)}
                    error={!!errors[SIGNER_FIELDS[role]]}
                    placeholder="Name"
                    disabled={submitted}
                    InputProps={{
                        endAdornment: !submitted && (
                            <InputAdornment position="end">
                                <IconButton size="small" edge="end" onClick={() => setEmployeeTarget(role)} title="Select from employees">
                                    <PersonSearchIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </Grid>
            <Grid item xs={5} sm={6}>
                <Box
                    sx={{
                        position: 'relative',
                        height: 40,
                        border: '1px solid',
                        borderColor: errors[`${role}Signature`] ? 'error.main' : '#ddd',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fff',
                        overflow: 'hidden',
                    }}
                >
                    {form[`${role}Signature`] ? (
                        <>
                            <Box
                                component="img"
                                src={form[`${role}Signature`]}
                                alt={label}
                                sx={{ maxHeight: '100%', maxWidth: '100%', cursor: submitted ? 'default' : 'pointer' }}
                                onClick={() => !submitted && setSignatureField(role)}
                            />
                            {!submitted && (
                                <IconButton
                                    size="small"
                                    onClick={() => update({ [`${role}Signature`]: '' })}
                                    sx={{ position: 'absolute', top: 0, right: 0 }}
                                    title="Clear signature"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                        </>
                    ) : (
                        <Button
                            size="small"
                            startIcon={<DrawIcon />}
                            onClick={() => setSignatureField(role)}
                            disabled={submitted}
                            color={errors[`${role}Signature`] ? 'error' : 'primary'}
                            sx={{ width: '100%', height: '100%' }}
                        >
                            Sign
                        </Button>
                    )}
                </Box>
            </Grid>
        </Grid>
    );

    // ─── Отправка ────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        const newErrors = {};
        const req = (field, value, message = 'Required') => {
            if (value === null || value === undefined || value === '') newErrors[field] = message;
        };

        req('requestedByName', form.requestedByName?.trim());
        req('jobCode', form.jobCode, 'Job is required');
        req('liftDate', form.liftDate, 'Date is required');
        req('qualifiedRiggerName', form.qualifiedRiggerName?.trim());
        req('certifiedOperatorName', form.certifiedOperatorName?.trim());
        req('craneType', form.craneType?.trim());
        req('craneManufacturer', form.craneManufacturer?.trim());
        req('craneModel', form.craneModel?.trim());
        req('craneSerialNo', form.craneSerialNo?.trim());
        req('annualInspectionDate', form.annualInspectionDate);
        req('itemsDescription', form.itemsDescription?.trim());
        req('weightSource', form.weightSource);
        req('cgMarked', form.cgMarked);
        req('maxOperatingRadius', form.maxOperatingRadius);
        req('plannedOperatingRadius', form.plannedOperatingRadius);
        req('allowableLoadAtMaxRadius', form.allowableLoadAtMaxRadius);
        req('clearanceToFacilities', form.clearanceToFacilities?.trim());
        req('clearPathForLoadMovement', form.clearPathForLoadMovement);
        req('groundAreaType', form.groundAreaType);
        req('matsRequired', form.matsRequired);
        req('undergroundUtilities', form.undergroundUtilities);
        req('riggingInspection', form.riggingInspection);
        req('dailyCraneInspection', form.dailyCraneInspection);

        if (form.groundAreaType === GROUND_TYPE.SOIL) {
            req('bearingCapacity', form.bearingCapacity);
        }

        for (const [field, label] of [
            ['maxOperatingRadius', 'Maximum Operating Radius'],
            ['plannedOperatingRadius', 'Planned Operating Radius'],
            ['allowableLoadAtMaxRadius', 'Allowable load at max radius'],
            ['bearingCapacity', 'Bearing Capacity'],
        ]) {
            const raw = form[field];
            if (raw !== '' && raw !== null && raw !== undefined && parseNumber(raw) === null) {
                newErrors[field] = `${label} must be a number`;
            }
        }

        if (!parseNumber(form.objectWeight)) newErrors.objectWeight = 'Weight of the object is required';
        if (!parseNumber(form.riggingWeightEstimate)) newErrors.riggingWeightEstimate = 'Estimated rigging weight is required';

        if (form.copiedFromUuid && !form.weightsConfirmed) {
            newErrors.objectWeight = 'Confirm the weights carried over from the previous lift';
            newErrors.riggingWeightEstimate = 'Confirm the weights carried over from the previous lift';
        }

        // Подписи заказчика и службы безопасности необязательные — их тут нет.
        if (!form.supervisorSignature) newErrors.supervisorSignature = 'Required';
        if (!form.riggerSignature) newErrors.riggerSignature = 'Required';
        if (!form.operatorSignature) newErrors.operatorSignature = 'Required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            const names = Object.keys(newErrors).map((field) => FIELD_LABELS[field] || field);
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                html: `Please complete:<ul style="text-align:left">${names.map((n) => `<li>${n}</li>`).join('')}</ul>`,
            });
            return;
        }

        // Просроченные инспекция и допуски подъём не запрещают, но подтвердить надо осознанно.
        const warnings = [];
        if (inspectionOverdue) {
            warnings.push(`Annual inspection for ${form.equipmentCode || 'this crane'} is overdue (${form.annualInspectionDate}).`);
        }
        if (form.operatorCertStatus === 'expired') {
            warnings.push(`Operator certification expired ${form.operatorCertExpires || ''}.`.trim());
        }
        if (form.riggerCertStatus === 'expired') {
            warnings.push(`Rigger certification expired ${form.riggerCertExpires || ''}.`.trim());
        }

        const confirm = await Swal.fire({
            title: 'Submit Lift Evaluation?',
            html: warnings.length
                ? `<b>Please confirm:</b><ul style="text-align:left">${warnings.map((w) => `<li>${w}</li>`).join('')}</ul>Submit anyway?`
                : 'Once submitted, the form cannot be edited.',
            icon: warnings.length ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonColor: warnings.length ? '#d33' : '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, submit it',
        });

        if (!confirm.isConfirmed) return;

        setErrors({});
        setSaving(true);

        try {
            await rememberIdentityIfNeeded();
            await liftEvaluationService.submit({ ...form, status: STATUS_SUBMITTED });
            await liftEvaluationService.deleteFromIndexedDB(form.uuid);

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Lift Evaluation Form submitted successfully',
            });

            navigate(returnTo || '/operations/lift-evaluation');
        } catch (error) {
            console.error('Error submitting form:', error);
            const missing = error.response?.data?.missing;
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: missing?.length
                    ? `The server rejected the form. Missing: ${missing.join(', ')}`
                    : (error.response?.data?.message || 'Failed to submit form'),
            });
        } finally {
            setSaving(false);
        }
    };

    // ─── Разметка ────────────────────────────────────────────────────────
    const isSoil = form.groundAreaType === GROUND_TYPE.SOIL;

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Шапка */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/operations/lift-evaluation')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h6" fontWeight={600} lineHeight={1.2}>Lift Evaluation Form</Typography>
                        <Typography variant="caption" color="text.secondary">Carry deck / mobile crane</Typography>
                    </Box>
                    {!isOnline && (
                        <Chip icon={<CloudOffIcon />} label="Offline" size="small" sx={{ ml: 'auto' }} />
                    )}
                </Box>

                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth size="small" label="Name" required
                            value={form.requestedByName || ''}
                            onChange={(e) => update({ requestedByName: e.target.value })}
                            error={!!errors.requestedByName}
                            placeholder="Select or type a name"
                            disabled={submitted}
                            InputProps={{
                                endAdornment: !submitted && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setEmployeeTarget('requestedBy')} title="Select from employees">
                                            <PersonSearchIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <Autocomplete
                            options={jobs}
                            getOptionLabel={(option) => `${option.Job || ''} - ${option.JobName || ''}`}
                            value={selectedJob}
                            onChange={handleJobChange}
                            disabled={submitted}
                            renderInput={(params) => (
                                <TextField {...params} label="Job" size="small" required
                                    error={!!errors.jobCode} helperText={errors.jobCode} />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth size="small" type="date" label="Date" required
                            value={form.liftDate || ''}
                            onChange={(e) => update({ liftDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.liftDate}
                            disabled={submitted}
                            onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Содержимое */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1.5, py: 1.25 }}>

                {form.copiedFromUuid && (
                    <Alert severity={form.weightsConfirmed ? 'info' : 'warning'} sx={{ mb: 2 }}>
                        {form.weightsConfirmed
                            ? 'Copied from a previous lift. The weights were confirmed — signatures and set-up photos are collected again.'
                            : 'Copied from a previous lift. Confirm the object and rigging weights before submitting.'}
                    </Alert>
                )}

                {/* Person in charge */}
                <Section title="Person in charge (PIC)" subtitle="Certifications are pulled from the training records">
                    {!isOnline && (
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                            No connection — certification dates cannot be pulled. Fill them in manually if you need them on the form.
                        </Alert>
                    )}
                    <Grid container spacing={1.5}>
                        {[
                            { role: 'rigger', label: 'Qualified Rigger', nameField: 'qualifiedRiggerName' },
                            { role: 'operator', label: 'Certified Operator', nameField: 'certifiedOperatorName' },
                        ].map(({ role, label, nameField }) => (
                            <Grid item xs={12} md={6} key={role}>
                                <TextField
                                    fullWidth size="small" required label={label}
                                    value={form[nameField] || ''}
                                    onChange={(e) => setSignerName(role, e.target.value)}
                                    error={!!errors[nameField]}
                                    helperText={errors[nameField]}
                                    disabled={submitted}
                                    InputProps={{
                                        endAdornment: !submitted && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setEmployeeTarget(role)} title="Select from employees">
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                {/* Пока человек не выбран, статус допуска не показываем. */}
                                {!!form[nameField] && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                                        <CertChip
                                            certification={form[`${role}CertCourse`] ? {
                                                course: form[`${role}CertCourse`],
                                                expirationDate: form[`${role}CertExpires`],
                                                status: form[`${role}CertStatus`],
                                            } : null}
                                            loading={certLoading[role]}
                                            offline={certOffline[role] && !form[`${role}CertCourse`]}
                                        />
                                        {form[`${role}CertCourse`] && (
                                            <Typography variant="caption" color="text.secondary">
                                                {form[`${role}CertCourse`]}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </Section>

                {/* Operating equipment */}
                <Section title="Operating equipment (mobile crane)">
                    <RadioGroup
                        row
                        value={form.equipmentSource}
                        onChange={(e) => {
                            const source = e.target.value;
                            // Арендный кран — другая машина: поля выбранного из справочника не переносим.
                            if (source === EQUIPMENT_SOURCE.EXTERNAL) {
                                setInspectionMissing(false);
                                setInspectionLookup(false);
                            }
                            update({
                                equipmentSource: source,
                                ...(source === EQUIPMENT_SOURCE.EXTERNAL ? {
                                    equipmentId: null,
                                    equipmentCode: '',
                                    craneType: '',
                                    craneManufacturer: '',
                                    craneModel: '',
                                    craneSerialNo: '',
                                    annualInspectionDate: '',
                                } : {}),
                            });
                        }}
                    >
                        <FormControlLabel value={EQUIPMENT_SOURCE.INTERNAL} control={<Radio size="small" />} label="Company crane" disabled={submitted} />
                        <FormControlLabel value={EQUIPMENT_SOURCE.EXTERNAL} control={<Radio size="small" />} label="Contractor / rental" disabled={submitted} />
                    </RadioGroup>

                    <Grid container spacing={1.5} sx={{ mt: 0 }}>
                        {form.equipmentSource === EQUIPMENT_SOURCE.INTERNAL && (
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={cranes}
                                    getOptionLabel={(o) => `${o.equipment || ''} — ${o.description || ''}`}
                                    value={selectedCrane}
                                    onChange={handleCraneChange}
                                    disabled={submitted}
                                    renderInput={(params) => <TextField {...params} label="Crane" size="small" />}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} md={form.equipmentSource === EQUIPMENT_SOURCE.INTERNAL ? 6 : 12}>
                            <TextField
                                fullWidth size="small" required label="Type of Crane"
                                value={form.craneType || ''}
                                onChange={(e) => update({ craneType: e.target.value })}
                                error={!!errors.craneType} disabled={submitted}
                            />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth size="small" required label="Manufacturer"
                                value={form.craneManufacturer || ''}
                                onChange={(e) => update({ craneManufacturer: e.target.value })}
                                error={!!errors.craneManufacturer} disabled={submitted}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth size="small" required label="Model No."
                                value={form.craneModel || ''}
                                onChange={(e) => update({ craneModel: e.target.value })}
                                error={!!errors.craneModel} disabled={submitted}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth size="small" required label="Serial Number"
                                value={form.craneSerialNo || ''}
                                onChange={(e) => update({ craneSerialNo: e.target.value })}
                                error={!!errors.craneSerialNo} disabled={submitted}
                            />
                        </Grid>

                        {canResolveIdentity && !submitted && (
                            <Grid item xs={12}>
                                <Alert
                                    severity="info"
                                    sx={{ py: 0 }}
                                    action={
                                        <Button
                                            size="small"
                                            startIcon={<AutoFixHighIcon />}
                                            onClick={handleResolveIdentity}
                                            disabled={resolvingIdentity || !isOnline}
                                        >
                                            {resolvingIdentity ? 'Reading…' : 'Pull from record'}
                                        </Button>
                                    }
                                >
                                    The equipment record stores this crane as “{selectedCrane.category}”.
                                    {isOnline ? ' We can try to read the manufacturer, model and serial from it.' : ' Reading it needs a connection.'}
                                </Alert>
                            </Grid>
                        )}

                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth size="small" required type="date" label="Latest Annual Inspection"
                                value={form.annualInspectionDate || ''}
                                onChange={(e) => update({ annualInspectionDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.annualInspectionDate}
                                disabled={submitted}
                                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                            />
                            {inspectionLookup && (
                                <Alert severity="info" icon={<CircularProgress size={14} />} sx={{ mt: 1, py: 0 }}>
                                    Checking the equipment record…
                                </Alert>
                            )}
                            {!inspectionLookup && inspectionMissing && !form.annualInspectionDate && (
                                <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                                    {isOnline
                                        ? 'The equipment record has no inspection date — enter it manually.'
                                        : 'No connection, and the device has no inspection date for this crane — enter it manually.'}
                                </Alert>
                            )}
                            {inspectionOverdue && (
                                <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                                    Inspection is overdue for the lift date — the lift is not blocked, but this will be recorded.
                                </Alert>
                            )}
                        </Grid>
                    </Grid>
                </Section>

                {/* Items to be lifted */}
                <Section title="Description of items to be lifted">
                    <DictationField
                        field="itemsDescription"
                        label="Items to be lifted"
                        rows={2}
                        value={form.itemsDescription}
                        onChange={(v) => update({ itemsDescription: v })}
                        dictation={dictation}
                        disabled={submitted}
                        required
                        error={!!errors.itemsDescription}
                        helperText={errors.itemsDescription}
                    />
                </Section>

                {/* Weight of object (A): один источник + вес */}
                <Section title="How weight of object obtained" subtitle="A — weight of the load itself">
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small" required error={!!errors.weightSource} disabled={submitted}>
                                <InputLabel shrink>Source</InputLabel>
                                <Select
                                    value={form.weightSource || ''}
                                    label="Source"
                                    displayEmpty
                                    notched
                                    onChange={(e) => update({ weightSource: e.target.value })}
                                >
                                    <MenuItem value=""><em>Not selected</em></MenuItem>
                                    {WEIGHT_SOURCES.map((source) => (
                                        <MenuItem key={source.key} value={source.key}>{source.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth size="small" required inputMode="decimal"
                                label="A — Weight of object (lbs)"
                                value={form.objectWeight || ''}
                                onChange={(e) => update({ objectWeight: e.target.value })}
                                error={!!errors.objectWeight}
                                helperText={errors.objectWeight}
                                disabled={submitted}
                            />
                        </Grid>
                    </Grid>
                </Section>

                {/* Center of gravity */}
                <Section title="Center of gravity (CG)">
                    <YesNoWithComment
                        question="CG will be marked onto load and a drawing included showing how it was determined"
                        value={form.cgMarked}
                        onChange={(v) => update({ cgMarked: v })}
                        comment={form.cgComment}
                        onCommentChange={(v) => update({ cgComment: v })}
                        field="cgComment"
                        dictation={dictation}
                        disabled={submitted}
                        required
                        error={!!errors.cgMarked}
                    />
                </Section>

                {/* Rigging: таблица, а оценка веса — под ней */}
                <Section title="Rigging equipment and crane attachments">
                    {RIGGING_CATEGORIES.map((category) => {
                        const rows = (form.riggingItems || [])
                            .map((item, index) => ({ item, index }))
                            .filter(({ item }) => item.category === category.key);

                        return (
                            <Box key={category.key} sx={{ mb: 0.75 }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary">
                                    {category.label}
                                </Typography>
                                {rows.map(({ item, index }, position) => (
                                    <Grid container spacing={1} key={index} sx={{ mt: 0.25 }} alignItems="center">
                                        <Grid item xs={3} sm={2}>
                                            <TextField fullWidth size="small" label={category.qtyLabel} inputMode="numeric"
                                                value={item.qty ?? ''}
                                                onChange={(e) => updateRiggingRow(index, { qty: e.target.value })}
                                                disabled={submitted} />
                                        </Grid>
                                        <Grid item xs={9} sm={5}>
                                            <TextField fullWidth size="small" label="Description"
                                                value={item.description || ''}
                                                onChange={(e) => updateRiggingRow(index, { description: e.target.value })}
                                                disabled={submitted} />
                                        </Grid>
                                        <Grid item xs={9} sm={4}>
                                            <TextField fullWidth size="small" label="Rated Capacity"
                                                value={item.ratedCapacity || ''}
                                                onChange={(e) => updateRiggingRow(index, { ratedCapacity: e.target.value })}
                                                disabled={submitted} />
                                        </Grid>
                                        {/* «+» есть только у строп и скоб — так размечен бланк. */}
                                        <Grid item xs={3} sm={1}>
                                            {!submitted && category.multiRow && (
                                                position === rows.length - 1 ? (
                                                    <IconButton size="small" onClick={() => addRiggingRow(category.key, index)} title="Add a row">
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton size="small" onClick={() => removeRiggingRow(index)} title="Remove this row">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            )}
                                        </Grid>
                                    </Grid>
                                ))}
                            </Box>
                        );
                    })}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {ASME_NOTE}
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    <TextField
                        fullWidth size="small" required inputMode="decimal"
                        label="B — Estimated rigging weight (lbs)"
                        value={form.riggingWeightEstimate || ''}
                        onChange={(e) => update({ riggingWeightEstimate: e.target.value })}
                        error={!!errors.riggingWeightEstimate}
                        helperText={errors.riggingWeightEstimate}
                        disabled={submitted}
                    />
                </Section>

                {/* Total */}
                <Section title="Weight of object, rigging equipment and crane attachments">
                    <TextField
                        fullWidth size="small" label="Total Weight (A + B)"
                        value={totalWeight === null ? '' : formatWeight(totalWeight)}
                        InputProps={{ readOnly: true }}
                        helperText="Calculated automatically"
                    />
                </Section>

                {/* Equipment and lift relationship */}
                <Section title="Equipment and lift relationship">
                    <Grid container spacing={1.5}>
                        <Grid item xs={6} md={3}>
                            <TextField fullWidth size="small" required label="A. Maximum Operating Radius (ft)" inputMode="decimal"
                                value={form.maxOperatingRadius || ''}
                                onChange={(e) => update({ maxOperatingRadius: e.target.value })}
                                error={!!errors.maxOperatingRadius} disabled={submitted} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField fullWidth size="small" required label="B. Planned Operating Radius (ft)" inputMode="decimal"
                                value={form.plannedOperatingRadius || ''}
                                onChange={(e) => update({ plannedOperatingRadius: e.target.value })}
                                error={!!errors.plannedOperatingRadius} disabled={submitted} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField fullWidth size="small" required label="C. Allowable load at max radius (lbs)" inputMode="decimal"
                                value={form.allowableLoadAtMaxRadius || ''}
                                onChange={(e) => update({ allowableLoadAtMaxRadius: e.target.value })}
                                error={!!errors.allowableLoadAtMaxRadius} disabled={submitted} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField fullWidth size="small" required label="F. Clearance to Facilities / Utilities (ft)"
                                value={form.clearanceToFacilities || ''}
                                onChange={(e) => update({ clearanceToFacilities: e.target.value })}
                                error={!!errors.clearanceToFacilities} disabled={submitted} />
                        </Grid>
                        <Grid item xs={12}>
                            <YesNoWithComment
                                label="G. Clear Path for Load Movement"
                                value={form.clearPathForLoadMovement}
                                onChange={(v) => update({ clearPathForLoadMovement: v })}
                                comment={form.clearPathComment}
                                onCommentChange={(v) => update({ clearPathComment: v })}
                                field="clearPathComment"
                                dictation={dictation}
                                disabled={submitted}
                                required
                                error={!!errors.clearPathForLoadMovement}
                            />
                        </Grid>
                    </Grid>
                </Section>

                {/* Ground */}
                <Section title="Stability of ground area">
                    <RadioGroup row value={form.groundAreaType} onChange={(e) => update({ groundAreaType: e.target.value })}>
                        <FormControlLabel value={GROUND_TYPE.SOIL} control={<Radio size="small" />} label="Soil" disabled={submitted} />
                        <FormControlLabel value={GROUND_TYPE.CONCRETE} control={<Radio size="small" />} label="Concrete" disabled={submitted} />
                    </RadioGroup>

                    <Grid container spacing={1.5} sx={{ mt: 0 }}>
                        {/* Несущая способность спрашивается только по грунту. */}
                        {isSoil && (
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth size="small" required label="Bearing Capacity (psf)" inputMode="decimal"
                                    value={form.bearingCapacity || ''}
                                    onChange={(e) => update({ bearingCapacity: e.target.value })}
                                    error={!!errors.bearingCapacity} disabled={submitted} />
                            </Grid>
                        )}

                        <Grid item xs={12} md={isSoil ? 6 : 12}>
                            <YesNoField
                                label="Mats Required"
                                value={form.matsRequired}
                                onChange={(v) => update({ matsRequired: v })}
                                disabled={submitted}
                                required
                                error={!!errors.matsRequired}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="Mats Size & Number"
                                value={form.matsSizeAndNumber || ''}
                                onChange={(e) => update({ matsSizeAndNumber: e.target.value })}
                                disabled={submitted} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DictationField
                                field="matsComment" label="Mats Comments" rows={1}
                                value={form.matsComment}
                                onChange={(v) => update({ matsComment: v })}
                                dictation={dictation} disabled={submitted}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <YesNoWithComment
                                label="Underground Utilities"
                                value={form.undergroundUtilities}
                                onChange={(v) => update({ undergroundUtilities: v })}
                                comment={form.undergroundUtilitiesComment}
                                onCommentChange={(v) => update({ undergroundUtilitiesComment: v })}
                                field="undergroundUtilitiesComment"
                                dictation={dictation}
                                disabled={submitted}
                                required
                                error={!!errors.undergroundUtilities}
                            />
                        </Grid>
                    </Grid>
                </Section>

                {/* Lifting operation */}
                <Section
                    title="Lifting operation"
                    subtitle="Optional — attach a photo of the set-up instead of a drawing"
                    action={!submitted && (
                        <Button size="small" startIcon={<AddAPhotoIcon />} onClick={() => setCameraOpen(true)}>
                            Add photo
                        </Button>
                    )}
                >
                    <DictationField
                        field="liftingOperationNotes" label="Notes" rows={1}
                        value={form.liftingOperationNotes}
                        onChange={(v) => update({ liftingOperationNotes: v })}
                        dictation={dictation} disabled={submitted}
                    />

                    {(form.liftingOperationPhotos || []).length > 0 && (
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                            {form.liftingOperationPhotos.map((raw, index) => {
                                const photo = photoOf(raw);
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Box sx={{ position: 'relative' }}>
                                            <Box component="img" src={photo.path} alt={`Lift set-up ${index + 1}`}
                                                sx={{ width: '100%', borderRadius: 1, border: '1px solid #ddd' }} />
                                            {!submitted && (
                                                <IconButton size="small" onClick={() => removePhoto(index)}
                                                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <DictationField
                                            field={`photo_${index}_caption`}
                                            label={`Photo ${index + 1} caption`}
                                            rows={1}
                                            value={photo.caption}
                                            onChange={(v) => updatePhotoCaption(index, v)}
                                            dictation={dictation}
                                            disabled={submitted}
                                            sx={{ mt: 1 }}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Section>

                {/* Inspection of contractors equipment — два пункта */}
                <Section title="Inspection of contractors equipment">
                    <Box sx={{ mb: 2 }}>
                        <YesNoWithComment
                            label="Rigging Inspection"
                            value={form.riggingInspection}
                            onChange={(v) => update({ riggingInspection: v })}
                            comment={form.riggingInspectionComment}
                            onCommentChange={(v) => update({ riggingInspectionComment: v })}
                            field="riggingInspectionComment"
                            dictation={dictation}
                            disabled={submitted}
                            required
                            error={!!errors.riggingInspection}
                        />
                    </Box>
                    <YesNoWithComment
                        label="Daily Crane Inspection completed"
                        value={form.dailyCraneInspection}
                        onChange={(v) => update({ dailyCraneInspection: v })}
                        comment={form.dailyCraneInspectionComment}
                        onCommentChange={(v) => update({ dailyCraneInspectionComment: v })}
                        field="dailyCraneInspectionComment"
                        dictation={dictation}
                        disabled={submitted}
                        required
                        error={!!errors.dailyCraneInspection}
                    />
                </Section>

                {/* Подписи: три обязательные и две необязательные */}
                <Section title="Lift approval signatures" subtitle="The first three are required to submit">
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={6}>{renderSignature('supervisor', '1. Supervisor')}</Grid>
                        <Grid item xs={12} md={6}>{renderSignature('rigger', '2. Rigger')}</Grid>
                        <Grid item xs={12} md={6}>{renderSignature('operator', '3. Operator')}</Grid>
                        <Grid item xs={12} md={6}>{renderSignature('customer', '4. Customer', true)}</Grid>
                        <Grid item xs={12} md={6}>{renderSignature('safety', '5. Safety', true)}</Grid>
                    </Grid>
                </Section>

                {/* Получатели */}
                <Section
                    title="Send the completed form to"
                    subtitle="These people are added to the recipients configured by the office"
                    action={(form.emailRecipients || []).length > 0 && (
                        <Chip
                            size="small"
                            label={`${form.emailRecipients.length} / ${MAX_RECIPIENTS}`}
                            color={form.emailRecipients.length >= MAX_RECIPIENTS ? 'warning' : 'default'}
                        />
                    )}
                >
                    {!submitted && (
                        <>
                            {/* Сотрудник — первым: так его и просили поставить выше ручного ввода. */}
                            <Button
                                variant="outlined" startIcon={<PersonSearchIcon />} sx={{ mb: 1.5 }}
                                onClick={() => setEmployeeTarget('recipient')}
                            >
                                Add employee
                            </Button>

                            <Grid container spacing={1} alignItems="flex-start">
                                <Grid item xs={12} sm={9}>
                                    <TextField
                                        fullWidth size="small" label="Add an email address" type="email"
                                        value={manualEmail}
                                        onChange={(e) => setManualEmail(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManualRecipient(); } }}
                                        error={manualEmail.trim() !== '' && !isValidEmail(manualEmail)}
                                        helperText={manualEmail.trim() !== '' && !isValidEmail(manualEmail) ? 'Not a valid email address' : undefined}
                                        inputProps={{ maxLength: 254 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Button fullWidth variant="outlined" size="medium" onClick={addManualRecipient}>Add</Button>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {(form.emailRecipients || []).length ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {form.emailRecipients.map((recipient) => (
                                <Chip
                                    key={recipient.email}
                                    label={`${recipient.name || recipient.email} · ${recipient.email}`}
                                    onDelete={submitted ? undefined : () => removeRecipient(recipient.email)}
                                    size="small"
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            No extra recipients — the form goes to whoever is set up in the office.
                        </Typography>
                    )}
                </Section>
            </Box>

            {/* Подвал */}
            {!submitted && (
                <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #e0e0e0', p: 2, flexShrink: 0 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            Total: <strong>{formatWeight(totalWeight)}</strong>
                        </Typography>
                        <Button
                            variant="contained" color="primary" size="large"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={saving}
                            sx={{ minWidth: 200 }}
                        >
                            {saving ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Модалки */}
            <SignatureModalWithAuto
                open={!!signatureField}
                onClose={() => setSignatureField(null)}
                sigPadRef={sigPadRef}
                employeeName={signatureField ? signerName(signatureField) : ''}
                onAccept={handleSignatureAccept}
                existingSignature={signatureField ? form[`${signatureField}Signature`] : null}
            />

            <SelectSingleEmployeeModal
                open={!!employeeTarget}
                onClose={() => setEmployeeTarget(null)}
                onSelect={handleEmployeeSelect}
                title={{
                    requestedBy: 'Select Person Requesting the Lift',
                    rigger: 'Select Qualified Rigger',
                    operator: 'Select Certified Operator',
                    supervisor: 'Select Supervisor',
                    customer: 'Select Customer Representative',
                    safety: 'Select Safety',
                    recipient: 'Select Recipient',
                }[employeeTarget] || 'Select Employee'}
            />

            <CameraCapture
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handlePhotoCapture}
            />
        </Box>
    );
};

export default LiftEvaluationForm;
